import NaviTable from '@cp/src/components/NaviTable/NaviTable';
import React, { useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  FIELD_CALL_INTERACTIONS,
  getFieldCallsInteractionsColDefs
} from '../../constants/communicationHistory.constant';
import DateTimePickerComponent from '@cp/src/components/DateTimePicker/DateTimePickerComponent';
import { DATE_TIME_TYPE } from '@cp/src/components/DateTimePicker/constants';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '@cp/src/service/clickStream.constant';
import { useDispatch, useSelector } from 'react-redux';
import {
  FROM_TIME,
  PREVIOUS_LOAN_ACCOUNT_INTERACTIONS,
  TO_TIME
} from '../PreviousLoanCommunicationHistory/constants';
import { RootState } from '@cp/src/store';
import { TAB_KEYS } from '../../constants';
import { fetchFieldCallInteractions } from '../../actions/interactionsActions';
import { readQueryParams } from '@cp/src/utils/QueryParamsHelper';
import styles from './index.module.scss';
import cx from 'classnames';
import { isObjectEmpty } from '@cp/src/utils/commonUtils';
import { QueryParams } from '@cp/src/components/interfaces';
import {
  buildUpdatedUrl,
  getDefaultDates,
  getDisbursementDate,
  createPayloadForFieldCallInteractions
} from '../../constants/utils';
import { createKey } from '@cp/src/utils/CaseDetail.utils';

const FieldCallInteractions = () => {
  const { loanId = '', customerId = '', selectedTab = '', caseReferenceId = '' } = useParams();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isPreviousLoanInteraction = selectedTab === TAB_KEYS.PREV_LOAN_INTERACTIONS;

  const { maxInputDate, defaultLastMonthDate, defaultPrevLoanFrom } = getDefaultDates();

  const pageData = useSelector(
    (state: RootState) => state.caseDetail?.pageData?.[createKey(loanId, customerId)]
  );

  const fieldCallInteractionsData = pageData?.fieldCallInteractions?.data?.data || [];
  const fieldCallInteractionsLoading = pageData?.fieldCallInteractions?.loading;
  const totalElements = pageData?.fieldCallInteractions?.data?.pages?.totalElements;

  const startDate = pageData?.details?.data?.startDate || '';
  const disbursementDate = getDisbursementDate(startDate);

  const [searchParams] = useSearchParams();
  const {
    [FIELD_CALL_INTERACTIONS]: queryParams,
    [PREVIOUS_LOAN_ACCOUNT_INTERACTIONS]: previousLoanAccountParams,
    ...rest
  } = readQueryParams();

  const getCallInteractionsData = () => {
    const payload = createPayloadForFieldCallInteractions({
      isPreviousLoanInteraction,
      queryParams,
      previousLoanAccountParams,
      caseReferenceId,
      defaultLastMonthDate,
      maxInputDate,
      defaultPrevLoanFrom
    });

    if (payload?.caseReferenceId) {
      dispatch(fetchFieldCallInteractions(loanId, customerId, payload));
    }
  };

  useEffect(() => {
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Field_Interactions_CallFeedbackPageLand);
    getCallInteractionsData();
  }, []);

  useEffect(() => {
    if (!isObjectEmpty(queryParams) || isPreviousLoanInteraction) {
      getCallInteractionsData();
    }
  }, [searchParams]);

  const updateQueryParamsAndNavigate = (params?: QueryParams) => {
    const url = buildUpdatedUrl({ queryParams, previousLoanAccountParams, rest, params });
    navigate(url, { replace: true });
  };

  const handleTimeChange = (
    val: React.ChangeEvent<HTMLInputElement>,
    filterKey: string,
    clear = false
  ) => {
    const value = clear ? disbursementDate : val?.target?.value;
    updateQueryParamsAndNavigate({ [filterKey]: value });
  };

  return (
    <div className="flex w-full flex-col h-full gap-[20px]">
      <div className="flex justify-between mr-3">
        <div className={cx('flex gap-[8px]', styles.datePicker)}>
          <DateTimePickerComponent
            heading="From"
            type={DATE_TIME_TYPE.DATE_TIME}
            id={FROM_TIME}
            name={FROM_TIME}
            max={queryParams?.[TO_TIME] || maxInputDate}
            min={disbursementDate}
            value={queryParams?.[FROM_TIME] || defaultLastMonthDate}
            onChange={e => handleTimeChange(e, FROM_TIME, e.target.value === '')}
            disabled={fieldCallInteractionsLoading}
          />
          <DateTimePickerComponent
            heading="To"
            type={DATE_TIME_TYPE.DATE_TIME}
            id={TO_TIME}
            name={TO_TIME}
            max={maxInputDate}
            min={queryParams?.[FROM_TIME] || defaultLastMonthDate}
            value={queryParams?.[TO_TIME] || maxInputDate}
            onChange={e => handleTimeChange(e, TO_TIME)}
            className={styles.datePicker}
            disabled={fieldCallInteractionsLoading}
          />
        </div>
      </div>
      <div className="flex-1 min-h-0 pr-1">
        <NaviTable
          tableName={FIELD_CALL_INTERACTIONS}
          tableClasses="!w-auto"
          tableContainerClasses="!h-[600px]"
          columns={getFieldCallsInteractionsColDefs()}
          data={fieldCallInteractionsData}
          totalElements={totalElements}
          loading={fieldCallInteractionsLoading}
          enableRowClick={() => false}
          defaultPaginationState={{
            pageIndex: Number(queryParams?.pageIndex || 0),
            pageSize: Number(queryParams?.pageSize || 20)
          }}
          onPaginationChange={({ pageIndex, pageSize }) =>
            updateQueryParamsAndNavigate({ pageIndex, pageSize })
          }
          bodyRowStyles={() => styles.tableRow}
          showPaginationComponent
        />
      </div>
    </div>
  );
};

export default FieldCallInteractions;
